import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Footprints, X } from "lucide-react";
import { useT } from "@/lib/i18n";

const DISABLED_KEY = "club-tour-disabled";
export const isTourDisabled = () => localStorage.getItem(DISABLED_KEY) === "1";
export const setTourDisabled = (v) => (v ? localStorage.setItem(DISABLED_KEY, "1") : localStorage.removeItem(DISABLED_KEY));

const PAD = 6;
const measure = (selector) => {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width === 0 && r.height === 0 ? null : { top: r.top, left: r.left, width: r.width, height: r.height };
};

// Knop in de clubkop: start de rondleiding; lichtgrijs als de rondleiding is uitgeschakeld.
export function TourButton({ onStart, disabled }) {
  const t = useT();
  return (
    <button onClick={onStart} data-testid="club-tour-btn" aria-pressed={!disabled}
      title={disabled ? t("Rondleiding uitgeschakeld — klik om weer in te schakelen en te starten") : t("Korte rondleiding langs de tabs van deze club")}
      className={`btn-brutal px-3 py-2 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 ${disabled ? "bg-muted text-muted-foreground/60 border-muted-foreground/40 opacity-70" : "bg-background text-foreground"}`}>
      <Footprints className="w-4 h-4" /> {t("Rondleiding")}
    </button>
  );
}

// Spotlight + tekstballon langs elementen (op selector). Stappen zonder zichtbaar doel worden overgeslagen.
export default function ClubTour({ steps, open, onClose, onDisable }) {
  const t = useT();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const visible = steps.filter((s) => document.querySelector(s.target));
  const step = visible[i];

  useEffect(() => { if (open) setI(0); }, [open]);

  const place = useCallback(() => { if (step) setRect(measure(step.target)); }, [step]);
  useLayoutEffect(() => {
    if (!open || !step) return undefined;
    document.querySelector(step.target)?.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
    const id = setTimeout(place, 350);
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { clearTimeout(id); window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open, step, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setI((v) => Math.min(v + 1, visible.length - 1));
      if (e.key === "ArrowLeft") setI((v) => Math.max(v - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, visible.length]);

  if (!open || !step || !rect) return null;
  const vw = window.innerWidth, vh = window.innerHeight;
  const popW = Math.min(380, vw - 24);
  const below = rect.top + rect.height + 16 + 190 < vh;
  const popTop = below ? rect.top + rect.height + PAD + 12 : Math.max(12, rect.top - PAD - 12 - 190);
  const popLeft = Math.min(Math.max(12, rect.left + rect.width / 2 - popW / 2), vw - popW - 12);
  const last = i === visible.length - 1;

  return (
    <div data-testid="club-tour" role="dialog" aria-label={t("Rondleiding")}>
      <div className="fixed inset-0 z-[70]" onClick={onClose} />
      <div className="fixed z-[71] border-2 border-primary pointer-events-none transition-all duration-300"
        style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2, boxShadow: "0 0 0 9999px rgba(10,10,10,0.62)" }} />
      <div className="fixed z-[72] bg-card border-2 border-foreground heavy-shadow p-4 transition-all duration-300" style={{ top: popTop, left: popLeft, width: popW }} data-testid="club-tour-popover">
        <div className="flex items-start justify-between gap-2">
          <div className="stat-label" data-testid="club-tour-progress">{t("Rondleiding")} · {i + 1}/{visible.length}</div>
          <button onClick={onClose} aria-label={t("Sluiten")} data-testid="club-tour-close" className="p-1 -mr-1 -mt-1 hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <h3 className="font-display font-black uppercase tracking-tight text-lg mt-1" data-testid="club-tour-title">{step.title}</h3>
        <p className="text-sm text-muted-foreground mt-1" data-testid="club-tour-text">{step.text}</p>
        <div className="flex items-center justify-between gap-2 mt-4 flex-wrap">
          <button onClick={() => { onDisable(); onClose(); }} data-testid="club-tour-disable" className="text-[11px] text-muted-foreground underline">{t("Rondleiding uitschakelen")}</button>
          <div className="flex gap-2">
            <button onClick={() => setI(Math.max(i - 1, 0))} disabled={i === 0} data-testid="club-tour-prev"
              className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1 disabled:opacity-40"><ChevronLeft className="w-3.5 h-3.5" /> {t("Vorige")}</button>
            <button onClick={() => (last ? onClose() : setI(i + 1))} data-testid="club-tour-next"
              className="btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1">{last ? t("Klaar") : t("Volgende")} {!last && <ChevronRight className="w-3.5 h-3.5" />}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
